import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaLinkedin, FaGithub, FaBars, FaTimes } from 'react-icons/fa';
import { HiOutlineDocumentText, HiOutlineEnvelope } from 'react-icons/hi2';
import { Link } from 'react-scroll';
import { contact } from '../content/data';

const navItems = ['Home', 'Featured', 'Projects', 'Courses', 'About'];

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sections = navItems.map(item => item.toLowerCase());
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-80px 0px -50% 0px' }
    );

    sections.forEach(section => {
      const element = document.getElementById(section);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-page/95 backdrop-blur-md border-b border-card-border shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="section-container">
          <div className="flex items-center justify-between h-16">
            <motion.div
              className="flex-shrink-0"
              whileHover={{ scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 400, damping: 10 }}
            >
              <Link
                to="home"
                smooth={true}
                duration={600}
                className="text-2xl font-bold gradient-text cursor-pointer"
              >
                CT
              </Link>
            </motion.div>

            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item}
                  to={item.toLowerCase()}
                  smooth={true}
                  duration={600}
                  offset={-80}
                  className={`nav-link cursor-pointer ${
                    activeSection === item.toLowerCase() ? 'nav-link-active' : ''
                  }`}
                >
                  {item}
                </Link>
              ))}
            </div>

            <div className="hidden md:flex items-center space-x-3">
              <a
                href={contact.resume}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-300 hover:text-indigo-400 transition-colors"
                aria-label="Open resume"
                title="Resume"
              >
                <HiOutlineDocumentText className="text-2xl" aria-hidden="true" focusable="false" />
              </a>
              <a
                href={`mailto:${contact.email}`}
                className="p-2 text-indigo-400 hover:text-indigo-300 transition-colors"
                aria-label="Email me"
                title="Contact"
              >
                <HiOutlineEnvelope className="text-2xl" aria-hidden="true" focusable="false" />
              </a>
            </div>

            <button
              className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
              onClick={closeMobileMenu}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 h-full w-72 bg-page-light border-l border-card-border z-50 md:hidden"
            >
              <div className="flex flex-col h-full p-6">
                <div className="flex justify-end mb-8">
                  <button
                    onClick={closeMobileMenu}
                    className="p-2 text-gray-400 hover:text-white"
                    aria-label="Close menu"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>

                <div className="flex flex-col space-y-2">
                  {navItems.map((item) => (
                    <Link
                      key={item}
                      to={item.toLowerCase()}
                      smooth={true}
                      duration={600}
                      offset={-80}
                      onClick={closeMobileMenu}
                      className={`nav-link text-lg cursor-pointer ${
                        activeSection === item.toLowerCase() ? 'nav-link-active' : ''
                      }`}
                    >
                      {item}
                    </Link>
                  ))}
                </div>

                <div className="mt-auto space-y-4">
                  <a
                    href={contact.resume}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 p-2 text-gray-300 hover:text-indigo-400 transition-colors"
                    aria-label="Open resume"
                  >
                    <HiOutlineDocumentText className="text-2xl" aria-hidden="true" focusable="false" />
                    <span className="sr-only">Resume</span>
                  </a>
                  <a
                    href={`mailto:${contact.email}`}
                    className="w-full flex items-center justify-center gap-2 p-2 text-indigo-400 hover:text-indigo-300 transition-colors"
                    aria-label="Email me"
                  >
                    <HiOutlineEnvelope className="text-2xl" aria-hidden="true" focusable="false" />
                    <span className="sr-only">Contact</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
